import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'core/store/Store';

/** One Break row as edited in the Settings tab. Times are "HH:mm" local. */
export interface BreakDraft {
  windowStart: string;
  windowEnd: string;
  durationMinutes: string;
}

/**
 * Run Settings while they are being edited. Every field is a string because it
 * is bound straight to an input; conversion and validation happen on save.
 */
export interface RunSettingsDraft {
  /** Free text naming the Run; defaults to the file name. */
  label: string;
  /** Empty on a Network Run and whenever the file carries the Depot columns. */
  depotLatitude: string;
  depotLongitude: string;
  shiftStart: string;
  shiftEnd: string;
  breaks: BreakDraft[];
}

/** Nominal Run Settings, used when the agency has no previous Run. */
export const defaultRunSettingsDraft: RunSettingsDraft = {
  label: '',
  depotLatitude: '',
  depotLongitude: '',
  shiftStart: '08:00',
  shiftEnd: '16:00',
  breaks: [],
};

export const emptyBreakDraft: BreakDraft = {
  windowStart: '12:00',
  windowEnd: '14:00',
  durationMinutes: '30',
};

/**
 * Only editing state lives here: which Run and which tab are open is read from
 * the URL (see `pages/capacityPlanning/useCapacityPlanUrl`).
 */
export interface CapacityPlanningState {
  settingsDraft: RunSettingsDraft;
}

const initialState: CapacityPlanningState = {
  settingsDraft: defaultRunSettingsDraft,
};

export const capacityPlanningSlice = createSlice({
  name: 'capacityPlanningSlice',
  initialState,
  reducers: {
    setSettingsDraft: (state, action: PayloadAction<RunSettingsDraft>) => ({
      ...state,
      settingsDraft: action.payload,
    }),
    patchSettingsDraft: (
      state,
      action: PayloadAction<Partial<RunSettingsDraft>>,
    ) => ({
      ...state,
      settingsDraft: { ...state.settingsDraft, ...action.payload },
    }),
    addBreak: (state) => ({
      ...state,
      settingsDraft: {
        ...state.settingsDraft,
        breaks: [...state.settingsDraft.breaks, emptyBreakDraft],
      },
    }),
    updateBreak: (
      state,
      action: PayloadAction<{ index: number; patch: Partial<BreakDraft> }>,
    ) => ({
      ...state,
      settingsDraft: {
        ...state.settingsDraft,
        breaks: state.settingsDraft.breaks.map((item, index) =>
          index === action.payload.index
            ? { ...item, ...action.payload.patch }
            : item,
        ),
      },
    }),
    removeBreak: (state, action: PayloadAction<number>) => ({
      ...state,
      settingsDraft: {
        ...state.settingsDraft,
        breaks: state.settingsDraft.breaks.filter(
          (_, index) => index !== action.payload,
        ),
      },
    }),
  },
});

export const selectSettingsDraft = (state: RootState) =>
  state.capacityPlanningReducer.settingsDraft;

export const {
  setSettingsDraft,
  patchSettingsDraft,
  addBreak,
  updateBreak,
  removeBreak,
} = capacityPlanningSlice.actions;

export default capacityPlanningSlice.reducer;
